/**
 * ReportWorkspace — the C3 entry surface for evaluation reports.
 *
 * Replaced the metadata-only EvaluationReports tab with a workspace that
 * drives the C2 generator: pick a template + format, click Generate, watch
 * the row appear in history. C4 (MYD-197) extracted the row list into
 * ReportHistory and added ReportPreview — this component now auto-opens
 * the preview on a successful generate so the user sees the artefact
 * immediately.
 *
 * Linear: MYD-196 (C3) + MYD-197 (C4).
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FileText, Plus, Loader2 } from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'

import {
	fetchReports,
	listReportTemplates,
	generateReport,
	deleteReport,
	clearLastGenerated,
} from 'slices/reportSlice'
import {
	sourceKey,
	type ReportFormat,
	type ReportRow,
	type TemplateDescriptor,
} from 'services/reports/reportService'
import { toast as toastApi } from 'components/ui/toast-store'

import ReportHistory from './ReportHistory'
import ReportPreview from './ReportPreview'

interface ReportWorkspaceProps {
	evalId: string
}

const SCOPE = 'evaluation' as const

const ReportWorkspace = ({ evalId }: ReportWorkspaceProps) => {
	const dispatch = useDispatch()
	const key = sourceKey(SCOPE, evalId)

	const reports: ReportRow[] = useSelector(
		(state: any) => state.reports?.bySource?.[key] || [],
	)
	const templates: TemplateDescriptor[] = useSelector(
		(state: any) => state.reports?.templatesBySource?.[key] || [],
	)
	const templatesLoaded: boolean = useSelector(
		(state: any) => Boolean(state.reports?.templatesLoadedBySource?.[key]),
	)
	const isGenerating: boolean = useSelector(
		(state: any) => Boolean(state.reports?.isGenerating),
	)
	const lastGeneratedId: string | null = useSelector(
		(state: any) => state.reports?.lastGeneratedId ?? null,
	)

	const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('')
	const [selectedFormat, setSelectedFormat] = useState<ReportFormat>('PDF')
	const [previewReport, setPreviewReport] = useState<ReportRow | null>(null)
	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message, variant }: { message: string; variant: 'success' | 'destructive' }) =>
		variant === 'success' ? toastApi.success(message, { title: 'Success' }) : toastApi.error(message, { title: 'Error' })

	useEffect(() => {
		// @ts-ignore — thunks
		dispatch(fetchReports({ scope: SCOPE, entityId: evalId }))
		// @ts-ignore — thunks
		dispatch(listReportTemplates({ scope: SCOPE, entityId: evalId }))
	}, [dispatch, evalId])

	// Default the template selection once templates arrive.
	useEffect(() => {
		if (!selectedTemplateKey && templates.length > 0) {
			setSelectedTemplateKey(templates[0].key)
		}
	}, [templates, selectedTemplateKey])

	const selectedTemplate = useMemo(
		() => templates.find((t) => t.key === selectedTemplateKey),
		[templates, selectedTemplateKey],
	)

	// Whenever the chosen template doesn't support the chosen format,
	// fall back to the first format the template does support so Generate
	// stays clickable.
	useEffect(() => {
		if (!selectedTemplate) return
		if (!selectedTemplate.supportedFormats.includes(selectedFormat)) {
			setSelectedFormat(selectedTemplate.supportedFormats[0])
		}
	}, [selectedTemplate, selectedFormat])

	// Auto-open preview when a generate succeeds (C4 acceptance criterion).
	// Track the last id we already opened so revisits don't re-trigger after
	// closing the preview, and don't trigger for legacy rows that happen to
	// share an id when the slice rehydrates.
	const lastOpenedIdRef = useRef<string | null>(null)
	useEffect(() => {
		if (!lastGeneratedId) return
		if (lastOpenedIdRef.current === lastGeneratedId) return
		const row = reports.find((r) => r._id === lastGeneratedId)
		if (!row || row.status !== 'READY') return
		lastOpenedIdRef.current = lastGeneratedId
		setPreviewReport(row)
	}, [lastGeneratedId, reports])

	const handleGenerate = useCallback(async () => {
		if (!selectedTemplateKey) return
		// @ts-ignore — thunks
		const result: any = await dispatch(
			generateReport({
				scope: SCOPE,
				entityId: evalId,
				templateKey: selectedTemplateKey,
				format: selectedFormat,
			}),
		)
		if (result?.error) {
			showToast({
				message: result.payload || 'Report generation failed',
				variant: 'destructive',
			})
		} else {
			showToast({
				message: `${selectedTemplate?.label ?? 'Report'} generated.`,
				variant: 'success',
			})
		}
	}, [dispatch, evalId, selectedTemplateKey, selectedFormat, selectedTemplate])

	const handleDelete = useCallback(
		async (reportId: string) => {
			if (!(await toastApi.confirm({ title: 'Delete report', message: 'Delete this report record?' })))
				return
			// @ts-ignore — thunks
			await dispatch(deleteReport({ scope: SCOPE, entityId: evalId, reportId }))
			if (reportId === lastGeneratedId) {
				dispatch(clearLastGenerated())
			}
			if (previewReport?._id === reportId) {
				setPreviewReport(null)
			}
		},
		[dispatch, evalId, lastGeneratedId, previewReport],
	)

	const handlePreview = useCallback((report: ReportRow) => {
		setPreviewReport(report)
	}, [])

	const formatChoices: ReportFormat[] = selectedTemplate?.supportedFormats ?? [
		'PDF',
		'DOCX',
		'HTML',
	]

	return (
		<div className="space-y-5">
			<div>
				<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
					<FileText className="w-5 h-5 text-[#0080fe]" />
					Reports
				</h3>
				<p className="text-sm text-gray-500">
					Pick a template, choose a format, and generate. Generated artefacts land in the
					history below for re-download or preview.
				</p>
			</div>

			{/* Generate panel */}
			<Card>
				<CardHeader className="pb-3">
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<Plus className="w-4 h-4" />
						New report
					</h4>
				</CardHeader>
				<CardContent>
					{!templatesLoaded ? (
						<div className="flex items-center gap-2 text-sm text-gray-500 py-3">
							<Loader2 className="w-4 h-4 animate-spin" />
							Loading templates…
						</div>
					) : templates.length === 0 ? (
						<div className="text-sm text-gray-500 py-3">
							No templates registered yet. Templates ship with C6.
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 md:items-end">
							<div className="space-y-1">
								<label
									htmlFor="report-template"
									className="text-xs font-medium text-gray-600 uppercase tracking-wide"
								>
									Template
								</label>
								<Select
									id="report-template"
									value={selectedTemplateKey}
									onChange={(e) => setSelectedTemplateKey(e.target.value)}
								>
									{templates.map((t) => (
										<option key={t.key} value={t.key}>
											{t.label}
										</option>
									))}
								</Select>
								{selectedTemplate?.description && (
									<p className="text-xs text-gray-500">{selectedTemplate.description}</p>
								)}
							</div>

							<div className="space-y-1">
								<label className="text-xs font-medium text-gray-600 uppercase tracking-wide block">
									Format
								</label>
								<div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
									{(['PDF', 'DOCX', 'HTML'] as ReportFormat[]).map((fmt) => {
										const supported = formatChoices.includes(fmt)
										const active = selectedFormat === fmt
										return (
											<button
												key={fmt}
												type="button"
												disabled={!supported}
												onClick={() => setSelectedFormat(fmt)}
												className={cn(
													'px-3 py-1.5 text-xs font-medium rounded transition-colors',
													active
														? 'bg-[#0080fe] text-white'
														: supported
															? 'text-gray-600 hover:bg-gray-100'
															: 'text-gray-300 cursor-not-allowed',
												)}
											>
												{fmt}
											</button>
										)
									})}
								</div>
							</div>

							<Button
								variant="primary"
								disabled={!selectedTemplateKey || isGenerating}
								onClick={handleGenerate}
								className="gap-1.5"
							>
								{isGenerating ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Generating…
									</>
								) : (
									<>
										<Plus className="w-4 h-4" />
										Generate
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			<ReportHistory
				scope={SCOPE}
				entityId={evalId}
				reports={reports}
				lastGeneratedId={lastGeneratedId}
				onPreview={handlePreview}
				onDelete={handleDelete}
			/>

			<ReportPreview
				open={Boolean(previewReport)}
				onClose={() => setPreviewReport(null)}
				scope={SCOPE}
				entityId={evalId}
				report={previewReport}
			/>

		</div>
	)
}

export default ReportWorkspace
